import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';
import {actions} from '../../../store';
import {ReduxState} from '../../../store/types';
import {Permission} from '../../../store/types/Permission';
import {AccountListItem, AccountsListFilters} from '../../../store/actions/accounts/payloads';
import PermissionProvider from '../../../components/PermissionProvider/PermissionProvider';
import PermissionPlaceholder from '../../../components/PermissionProvider/PermissionPlaceholder';
import Header from '../../../components/ListViewHeader/Header';
import DataGrid from '../../../components/DataGrid/DataGrid';
import {PaginationMode} from '../../../components/DataGrid/types';
import usePageTitle from '../../../hooks/usePageTitle';
import {generateAccountListColumns, useStyles} from './AccountsList.utils';
import FiltersComponent from './FiltersComponent';
import AddNewAccountDialog from '../AddNewAccountDialog/AddNewAccountDialog';
import TransactionsDialog from '../../../components/Accounts/TransactionsDialog';
import {getRoute, Routes} from '../../../routes/routes';

const initPageSize = 10;
const initPage = 0;

const AccountsList: React.VFC = () => {
    usePageTitle();
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const classes = useStyles();

    const [addDialogVisible, setAddDialogVisible] = useState(false);
    const [forceFirstPage, setForceFirstPage] = useState(false);
    const [pageSize, setPageSize] = useState(initPageSize);
    const [transactionsModalAccount, setTransactionsModalAccount] = useState<AccountListItem | null>(null);

    const {accountsList, isLoading} = useSelector((state: ReduxState) => state.accounts);
    const userDateFormat = useSelector<ReduxState, string>(
        (state) =>
            state.generic.globalCustomerInfo?.customer_info?.out_date_format || '',
    );

    const fetchData = (
        params?: {pageSize: number; page: number},
        filters?: AccountsListFilters,
    ) => {
        dispatch(
            actions.getAccountsList.request({
                limit: params?.pageSize ?? initPageSize,
                offset: params ? params.page * params.pageSize : 0,
                ...filters,
            }),
        );
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleEdit = (item: AccountListItem) => {
        history.push(getRoute(Routes.AccountDetails, {id: item.id}));
    };

    const handleTransactions = (item: AccountListItem) => {
        setTransactionsModalAccount(item);
    };

    const columns = useMemo(
        () => generateAccountListColumns(t, classes, handleEdit, handleTransactions, userDateFormat),
        [userDateFormat],
    );

    return (
        <PermissionPlaceholder permission={Permission.Accounts.value}>
            <div className={classes.mainContainer}>
                <div className={classes.scrollable}>
                    <div className={classes.content}>
                        <div className={classes.header}>
                            <PermissionProvider permission={Permission.Accounts.AddNewAccount.value}>
                                <Header
                                    title={t('screens:accounts.title')}
                                    totalNumber={accountsList.total}
                                    ofNumber={accountsList.activeTotal}
                                    // the 'O' total covers both active and not-yet-active
                                    // accounts, same as the status filter
                                    ofText={t('screens:accounts.statusActiveAndNotYetActive')}
                                    buttonText={t('common:addNewButton')}
                                    buttonOnClick={() => setAddDialogVisible(true)}
                                    plusIcon
                                    dataQa="accounts-view-header"
                                />
                            </PermissionProvider>
                        </div>

                        <FiltersComponent
                            page={initPage}
                            pageSize={pageSize}
                            handleSearch={fetchData}
                            handleForceFirstPage={() => setForceFirstPage(true)}
                        />

                        <DataGrid
                            columns={columns}
                            data={accountsList.items}
                            rowCount={accountsList.total}
                            loading={isLoading}
                            paginationMode={PaginationMode.Server}
                            centeredRows
                            className={classes.tableColumns}
                            onPageChange={({page, pageSize: size}) => {
                                setForceFirstPage(false);
                                setPageSize(size);
                                fetchData({page, pageSize: size}, accountsList.filters);
                            }}
                            onPageSizeChange={({pageSize: size}) => {
                                setForceFirstPage(true);
                                setPageSize(size);
                                fetchData({page: initPage, pageSize: size}, accountsList.filters);
                            }}
                            initialPageSize={initPageSize}
                            forceFirstPage={forceFirstPage}
                            showMessageWhenEmpty
                        />
                    </div>

                    <AddNewAccountDialog
                        isOpen={addDialogVisible}
                        toggleVisibility={() => setAddDialogVisible(false)}
                        onSuccess={() => setAddDialogVisible(false)}
                    />

                    {transactionsModalAccount && (
                        <TransactionsDialog
                            isOpen={!!transactionsModalAccount}
                            toggleVisibility={() => setTransactionsModalAccount(null)}
                            iAccount={transactionsModalAccount.i_account}
                            accountId={transactionsModalAccount.id}
                        />
                    )}
                </div>
            </div>
        </PermissionPlaceholder>
    );
};

export default AccountsList;
